/**
 * meta_templates.js
 * Template registry - metadata-driven presentation inside the conversation.
 *
 * One registration yields four artifacts, all derived from the same entry so
 * they cannot drift apart:
 *
 *   1. GRAMMAR   - the id joins a closed enum the model is constrained to emit
 *   2. CATALOG   - a `when` line teaches the model which view applies
 *   3. RENDER    - trusted markup, may instantiate web components
 *   4. TRANSCRIPT- a short line telling the model what the user was just shown
 *
 * The registry IS the back-channel. Grammar decides what is *emittable*; the
 * catalog decides what is *appropriate*. Registering a template widens both at
 * once, so the model can never name a view that cannot be rendered.
 *
 * TRUST
 *
 * `render()` output is inserted as trusted HTML by <m5t-chat-workflow>. Model
 * text is never trusted - it flows to <m5t-chat-response>, which is permanently
 * `html: false`. The trust boundary is the element type, not a flag, so no
 * config mistake can let model output emit markup. Inside a template, escape
 * every interpolated value with esc() / attr().
 *
 * NOTE: no bare imports here - `shared/` sits outside the pnpm workspaces. The
 * JMESPath engine is injected by the app, the same pattern m5t-chat-response.js
 * uses for markdown-it.
 */

/**
 * Abstention, as a first-class enum member rather than an absence.
 *
 * This started life as `null` in a `type: ['string','null']` enum, and the model
 * essentially never chose it - "what is a cat" routed to an account summary.
 * Two reasons, both fixed by naming it:
 *
 *   - every template got a descriptive catalog line while declining got half a
 *     clause, so abstention read as the unusual answer;
 *   - a nullable-enum schema is exactly the JSON Schema shape whose XGrammar
 *     coverage is least worth relying on. A plain string enum is unambiguous.
 *
 * Small models abstain markedly better when "none" is something they *say*.
 */
export const NO_TEMPLATE = 'NONE';

/**
 * On-domain questions that no view answers: "what does DRAFT mean?", "why won't
 * my domain verify?".
 *
 * Splitting this out of NONE is what stops the router pendulum. While NONE meant
 * "generate prose", it was always a plausible answer, so the model reached for
 * it - and every attempt to push it away then overrode correct positives. With
 * prose reachable only through EXPLAIN, NONE means "out of scope, decline", and
 * being wrong about it is visible rather than silently degraded.
 *
 * It also confines the hallucination surface. Every fabrication observed - a
 * claimed app creation, invented app names - came from the prose path. Now that
 * path runs only when the model has explicitly asked for it.
 */
export const EXPLAIN = 'EXPLAIN';

/** Every id the router may emit: registered targets, plus the two reserved ones. */
export function routingOptions() {
  return [...routableIds(), EXPLAIN];
}

/** Separator between a view id and one of its action ids in a routing decision. */
export const ACTION_SEP = ':';

/** @type {Map<string, object>} */
const registry = new Map();

/**
 * Normalize a params declaration to `{ name: {type, required, values} }`.
 *
 * Both forms are accepted so views keep the terse shorthand while actions, which
 * need `required`, can say so:
 *
 *   params: { period: ['Q1','Q2'] }                    // shorthand
 *   params: { url: { type: 'url', required: true } }   // full
 */
function normalizeParams(params = {}) {
  const out = {};

  for (const [name, spec] of Object.entries(params)) {
    if (Array.isArray(spec)) {
      out[name] = { type: 'string', required: false, values: spec };
    } else if (typeof spec === 'string') {
      out[name] = { type: spec, required: false, values: null };
    } else {
      out[name] = {
        type: spec?.type ?? 'string',
        required: !!spec?.required,
        values: Array.isArray(spec?.values) ? spec.values : null
      };
    }
  }

  return out;
}

/** @type {((expression: string, data: any) => any) | null} */
let queryEngine = null;

/**
 * Install the JMESPath search function: (expression, data) => result
 *
 *   import { search } from '@metrichor/jmespath';
 *   setQueryEngine((expr, data) => search(data, expr));
 *
 * With no engine installed, a `source` expression falls back to dot-path
 * traversal, which covers the common `a.b.c` case without the dependency.
 */
export function setQueryEngine(fn) {
  queryEngine = fn;
}

/** Escape text for insertion into trusted markup. */
export function esc(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Escape a value for an HTML attribute, JSON-encoding objects and arrays so a
 * template can hand structured data to a web component:
 *
 *   `<m5t-spend-table rows="${attr(rows)}"></m5t-spend-table>`
 */
export function attr(value) {
  const text = (value !== null && typeof value === 'object')
    ? JSON.stringify(value)
    : String(value ?? '');
  return esc(text);
}

/**
 * Register a template.
 *
 * @param {object} def
 * @param {string}   def.id          - Enum value the model may emit. SCREAMING_SNAKE.
 * @param {string}   def.when        - When this view applies, in plain language. Goes
 *                                     verbatim into the system prompt.
 * @param {object}   [def.params]    - name -> 'string' | 'number' | 'boolean' | string[]
 *                                     (an array is an allowed-value list). Validated in
 *                                     JS, deliberately NOT in the grammar - see
 *                                     toResponseSchema().
 * @param {string}   [def.source]    - JMESPath applied to the fetched payload before
 *                                     render, e.g. 'data.output.data.hcp_spend_info'.
 * @param {Function} [def.fetch]     - async (params) => payload. Omit for a template
 *                                     that presents data already in hand.
 * @param {Function} def.render      - (data, params) => trusted HTML string.
 * @param {Function} def.transcript  - (data, params) => short factual line pushed into
 *                                     the model transcript. REQUIRED: without it the
 *                                     model has no idea the user was shown anything,
 *                                     and the next turn ("sort that by spend") has no
 *                                     referent.
 * @param {string|string[]} [def.nav] - meta_nav token(s) that render this view,
 *                                     e.g. 'HOME_DASH'. Claiming a token here is the
 *                                     whole wiring - no switch case in app_events.
 * @param {string} [def.navPrompt]   - what the user "said" when they clicked that nav
 *                                     item, e.g. 'Show me the dashboard'. Defaults to a
 *                                     phrase built from the id.
 * @param {string[]} [def.tips]      - capability phrases shown while the user waits. Falls
 *                                     back to a "Try asking" line built from navPrompt.
 */
export function registerTemplate(def) {
  if (!def || typeof def !== 'object') throw new TypeError('[Templates] definition must be an object');
  if (!def.id || typeof def.id !== 'string') throw new TypeError('[Templates] id is required');
  if (def.id === NO_TEMPLATE) throw new TypeError(`[Templates] "${NO_TEMPLATE}" is reserved - it is how the model declines to route`);
  // Ids become string literals inside a generated EBNF grammar, so anything
  // outside this set could break the grammar or inject into it.
  if (!/^[A-Z][A-Z0-9_]*$/.test(def.id)) {
    throw new TypeError(`[Templates] "${def.id}" must be SCREAMING_SNAKE_CASE - ids are emitted into a grammar`);
  }
  if (!def.when || typeof def.when !== 'string') throw new TypeError(`[Templates] ${def.id}: when is required (it becomes the catalog line)`);
  if (typeof def.render !== 'function') throw new TypeError(`[Templates] ${def.id}: render must be a function`);
  if (typeof def.transcript !== 'function') throw new TypeError(`[Templates] ${def.id}: transcript must be a function - the model cannot see rendered markup`);

  if (registry.has(def.id)) {
    console.warn(`[Templates] "${def.id}" re-registered - replacing`);
  }

  const actions = {};
  for (const [actionId, a] of Object.entries(def.actions ?? {})) {
    if (!/^[A-Z][A-Z0-9_]*$/.test(actionId)) {
      throw new TypeError(`[Templates] action "${actionId}" must be SCREAMING_SNAKE_CASE - it is emitted into a grammar`);
    }
    if (typeof a?.run !== 'function') {
      throw new TypeError(`[Templates] ${def.id}.${actionId}: run must be a function`);
    }
    actions[actionId] = { ...a, params: normalizeParams(a.params) };
  }

  registry.set(def.id, {
    source: null,
    fetch: null,
    patterns: [],
    ...def,
    params: normalizeParams(def.params),
    actions,
    nav: def.nav ? (Array.isArray(def.nav) ? def.nav : [def.nav]) : []
  });

  return def.id;
}

/**
 * Every routable target: views, plus `VIEW:ACTION` for each declared action.
 *
 * Actions share the view enum rather than getting a pass of their own, so one
 * routing decision answers both "which subject" and "show or do" - the model
 * never has to hold an intent across two calls.
 */
export function routableIds() {
  const ids = [];

  for (const [id, def] of registry) {
    ids.push(id);
    for (const actionId of Object.keys(def.actions)) ids.push(`${id}${ACTION_SEP}${actionId}`);
  }

  return ids;
}

/** Split a routable id back into its parts. */
export function parseRoutable(routable) {
  const [componentId, action = null] = String(routable ?? '').split(ACTION_SEP);
  return { componentId, action };
}

/** An action definition, or null. */
export function getAction(componentId, actionId) {
  return registry.get(componentId)?.actions?.[actionId] ?? null;
}

/**
 * Short phrases describing what the app can do, for the waiting bubble.
 *
 * Sourced from the registry rather than hand-written, so the list cannot go
 * stale as views come and go - registering a template makes it discoverable in
 * the one place users are guaranteed to be looking, which is while they wait.
 *
 * Discovery is the standing problem with a conversational surface: nothing on
 * screen advertises what it will understand. A dead two seconds is the cheapest
 * airtime there is.
 *
 * @param {string[]} [extra] - app-level phrases not tied to any single view
 */
export function capabilityTips(extra = []) {
  const tips = [];

  for (const def of registry.values()) {
    if (Array.isArray(def.tips) && def.tips.length) {
      tips.push(...def.tips);
    } else if (def.navPrompt) {
      tips.push(`Try asking: "${def.navPrompt}"`);
    }
  }

  return [...new Set([...tips, ...extra])];
}

/**
 * What a navigation token means, as a conversational turn: `{ id, prompt }`.
 *
 * A nav click enters the conversation as a prompt the app speaks for the user
 * ("Show me the dashboard") rather than as a side-channel render. That keeps one
 * path for every way a view can be reached, and the transcript gets a real
 * question to pair the answer with instead of a dangling note.
 *
 * The `id` rides along so the turn arrives pre-resolved: a click is unambiguous
 * and should not be re-derived by a probabilistic router that could land
 * somewhere else.
 */
export function navIntent(token) {
  if (!token) return null;

  for (const def of registry.values()) {
    if (!def.nav.includes(token)) continue;
    return {
      id: def.id,
      prompt: def.navPrompt || `Show me the ${def.id.toLowerCase().replace(/_/g, ' ')}`
    };
  }

  return null;
}

export function unregisterTemplate(id) {
  return registry.delete(id);
}

export function getTemplate(id) {
  return registry.get(id) ?? null;
}

export function listTemplates() {
  return [...registry.values()];
}

export function templateIds() {
  return [...registry.keys()];
}

/** Test seam - drops every registration. */
export function clearTemplates() {
  registry.clear();
}

// ---------------------------------------------------------------------------
// 1. GRAMMAR - what the model may emit
// ---------------------------------------------------------------------------

/**
 * JSON Schema for a constrained routing response.
 *
 * Deliberately lopsided: `component_id` is a hard closed enum, `params` is an
 * open object. XGrammar (WebLLM's constrained decoder) covers a subset of JSON
 * Schema and discriminated `oneOf` unions are where it gets fragile - so the
 * grammar closes only the thing whose violation is unrecoverable. An
 * unregistered id is a crash; a malformed param is a validation message.
 * Params are checked by validateParams() instead.
 *
 * NO_TEMPLATE leads the enum: it is the answer for most messages, and listing it
 * first is free.
 */
export function toResponseSchema() {
  return {
    type: 'object',
    properties: {
      component_id: {
        type: 'string',
        enum: [NO_TEMPLATE, ...registry.keys()]
      },
      params: { type: 'object' }
    },
    required: ['component_id'],
    additionalProperties: false
  };
}

/**
 * Deterministic routing, tried BEFORE the model.
 *
 * Most of what users type at an admin surface is a small set of stock phrases.
 * "Show me my apps" has one correct answer and always will; handing it to a
 * quantized 8B to classify buys nothing and loses reliability. The model earns
 * its place on phrasings nobody enumerated - not on the ones we can.
 *
 * This is the same trade nav already makes: a click carries a resolved directive
 * and never misroutes. A matched pattern is that, for typed input.
 *
 * Patterns are declared on the template, so one registration still owns
 * everything about how a view is reached. Order matters - the first template
 * with a hit wins, and actions are checked before their own views so
 * "create a new app" cannot be swallowed by a bare /apps/ pattern.
 *
 * @returns {{id: string, via: 'pattern'}|null}
 */
export function matchPatterns(text) {
  const s = String(text ?? '').trim();
  if (!s) return null;

  // Actions first: they are the more specific reading of any message that
  // mentions the same subject.
  const targets = [];
  for (const [id, def] of registry) {
    for (const actionId of Object.keys(def.actions)) {
      const action = def.actions[actionId];
      if (action.patterns?.length) targets.push({ id: `${id}${ACTION_SEP}${actionId}`, patterns: action.patterns });
    }
  }
  for (const [id, def] of registry) {
    if (def.patterns.length) targets.push({ id, patterns: def.patterns });
  }

  for (const t of targets) {
    if (t.patterns.some((p) => p.test(s))) return { id: t.id, via: 'pattern' };
  }

  return null;
}

/**
 * Turn the model's marks into a decision.
 *
 * This is the whole routing decision, and it is deterministic. The model judges
 * each option in isolation - a task it does well - and the arithmetic of turning
 * several yes/no answers into one winner happens here, where it cannot be
 * contradicted.
 *
 * Precedence:
 *   1. An action beats its own view. "create a new app" marks both MY_APPS and
 *      MY_APPS:CREATE_APP, and the verb is the intent - the subject is only how
 *      it was found.
 *   2. A specific option beats EXPLAIN, which is the broad catch-all.
 *   3. Nothing marked means NONE - out of scope.
 *   4. Several unrelated options is genuine indecision. Declining is visible;
 *      picking one arbitrarily is the over-triggering this design exists to avoid.
 *
 * @param {object} marks - { OPTION_ID: boolean }
 * @returns {{id: string, marked: string[], ambiguous?: boolean}}
 */
export function decideFromMarks(marks = {}) {
  const marked = routingOptions().filter((id) => marks[id] === true);

  let specific = marked.filter((id) => id !== EXPLAIN);

  // Drop a bare view when one of its own actions is also marked.
  const actionOwners = new Set(
    specific.filter((id) => id.includes(ACTION_SEP)).map((id) => id.split(ACTION_SEP)[0])
  );
  specific = specific.filter((id) => id.includes(ACTION_SEP) || !actionOwners.has(id));

  if (specific.length === 1) return { id: specific[0], marked };
  if (specific.length > 1) return { id: NO_TEMPLATE, marked, ambiguous: true };
  if (marked.includes(EXPLAIN)) return { id: EXPLAIN, marked };

  return { id: NO_TEMPLATE, marked };
}

/** WebLLM takes `response_format.schema` as a JSON string. */
export function toResponseSchemaJSON() {
  return JSON.stringify(toResponseSchema());
}

/**
 * The routing decision as an EBNF grammar, for `response_format.type: 'grammar'`.
 *
 * Preferred over the JSON Schema above for routing. Both end up compiled to a
 * grammar that masks logits on the GPU, but the schema route depends on
 * XGrammar's JSON-Schema converter honouring `enum` - which is the one link that
 * cannot be verified from outside the engine. Writing the alternation directly
 * removes the question: a misroute is then provably the model's judgement and
 * not a translation gap, which is the only way prompt tuning means anything.
 *
 * Emits exactly `{"component_id":"SOME_ID"}` - roughly ten tokens, so the pass
 * is about as cheap as a generation gets.
 *
 * The entry rule MUST be called `root`. XGrammar's `Grammar.fromEBNF(str,
 * rootRule = "root")` defaults to that name; the WebLLM type docs illustrate
 * with `main ::=`, which compiles to nothing useful.
 *
 * Grammar notes for anyone extending this: `#` comments, C-style unicode
 * escapes, and no `A-B` (match-A-not-B) support.
 *
 * @returns {string|null} null when nothing is registered
 */
export function toRoutingGrammar() {
  if (registry.size === 0) return null;

  const views = routingOptions();

  // Booleans ONLY. The model no longer names a winner - the decision is derived
  // from these marks in code (see decideFromMarks).
  //
  // It emitted a `component_id` after the booleans until the marks proved the
  // model could not reference its own output: "create a new app" marked MY_APPS
  // and MY_APPS:CREATE_APP true and then committed NONE; "what does DRAFT mean"
  // marked EXPLAIN alone and committed APP_SNIPPET, an option it had just marked
  // FALSE. Under grammar constraint the id token is chosen by local probability,
  // not by re-reading the scratchpad, and an 8B at q4f32 will not self-reference
  // reliably.
  //
  // Each boolean is a binary question against one `when` line ("does MY_APPS
  // match this?") - far easier than an n-way choice, and something the model
  // demonstrably gets right. So ask only for that, and do the arithmetic here.
  const flags = views.map((id, i) => `"${i === 0 ? '{' : ','}\\"${id}\\":" bool`);
  flags.push('"}"');

  return [
    '# Routing decision. Generated from the template registry - do not hand-edit.',
    `root ::= ${flags.join(' ')}`,
    'bool ::= "true" | "false"'
  ].join('\n');
}

/**
 * A fully qualified address: scheme plus a real host.
 *
 * `new URL()` is the right tool *here*, unlike in domainOf - a scheme is
 * mandatory by rule, so throwing on a bare domain is the desired behaviour
 * rather than the problem.
 */
export function isFullyQualifiedUrl(value) {
  let url;
  try {
    url = new URL(String(value));
  } catch {
    return false;
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;

  const host = url.hostname;
  return host === 'localhost' || /^[^.]+(\.[^.]+)+$/.test(host);
}

/** Check a value against one normalized param spec. Returns an error string or null. */
function checkParam(key, spec, value) {
  if (spec.values) {
    return spec.values.includes(value) ? null : `${key}: "${value}" is not one of ${spec.values.join(', ')}`;
  }

  if (spec.type === 'url') {
    if (typeof value !== 'string') return `${key}: expected a URL string`;
    return isFullyQualifiedUrl(value)
      ? null
      : `${key}: "${value}" is not a fully qualified address (needs https:// and a real host)`;
  }

  // eslint-disable-next-line valid-typeof
  return typeof value === spec.type ? null : `${key}: expected ${spec.type}, got ${typeof value}`;
}

/**
 * Check params against a normalized spec. Unknown keys are dropped - the grammar
 * leaves this side open, so anything not declared is noise rather than an error.
 */
function checkParams(spec, params = {}) {
  const errors = [];
  const clean = {};

  for (const [key, rule] of Object.entries(spec)) {
    const value = params[key];

    if (value === undefined || value === null || value === '') {
      if (rule.required) errors.push(`${key} is required`);
      continue;
    }

    const err = checkParam(key, rule, value);
    if (err) errors.push(err);
    else clean[key] = value;
  }

  return { ok: errors.length === 0, params: clean, errors };
}

/** Check params for a view. */
export function validateParams(id, params = {}) {
  const def = registry.get(id);
  if (!def) return { ok: false, params: {}, errors: [`unknown template "${id}"`] };
  return checkParams(def.params, params);
}

/** Check params for an action. */
export function validateActionParams(componentId, actionId, params = {}) {
  const action = getAction(componentId, actionId);
  if (!action) return { ok: false, params: {}, errors: [`unknown action "${componentId}${ACTION_SEP}${actionId}"`] };
  return checkParams(action.params, params);
}

/**
 * JSON Schema for an action's arguments, for the extraction pass.
 *
 * Schema rather than EBNF here, unlike routing. EBNF was chosen there because
 * the enum translation was the one link I could not verify; arguments are plain
 * strings with nothing to mistranslate, and a hand-written EBNF would need
 * bounded repetition rules that risk truncating mid-JSON.
 */
/**
 * The declared params of a routable target - a view's own, or an action's.
 *
 * Views take params too (APP_SNIPPET needs a name), and extraction was
 * originally action-only because actions were the motivating case. Reading both
 * from one place is what lets the extraction pass serve either.
 */
export function paramsFor(componentId, actionId = null) {
  if (actionId) return getAction(componentId, actionId)?.params ?? null;
  return registry.get(componentId)?.params ?? null;
}

export function toActionSchema(componentId, actionId) {
  const params = paramsFor(componentId, actionId);
  if (!params) return null;

  const properties = {};
  const required = [];

  for (const [key, rule] of Object.entries(params)) {
    properties[key] = rule.values
      ? { type: 'string', enum: rule.values }
      : { type: rule.type === 'url' ? 'string' : rule.type };
    if (rule.required) required.push(key);
  }

  return {
    type: 'object',
    properties,
    ...(required.length ? { required } : {}),
    additionalProperties: false
  };
}

export function toActionSchemaJSON(componentId, actionId) {
  const schema = toActionSchema(componentId, actionId);
  return schema ? JSON.stringify(schema) : null;
}

// ---------------------------------------------------------------------------
// 2. CATALOG - when each view applies
// ---------------------------------------------------------------------------

/**
 * One line per registered view: `- ID: when (params: a, b)`.
 *
 * Returns null when nothing is registered, so a caller can drop the section
 * rather than emit a heading with no entries under it.
 */
export function catalogPrompt() {
  if (registry.size === 0) return null;

  const lines = [];

  for (const def of registry.values()) {
    const params = Object.keys(def.params);
    lines.push(`- ${def.id}: ${def.when}${params.length ? ` (params: ${params.join(', ')})` : ''}`);

    // Actions read as peers of views: the router is choosing an intent, and
    // "show me my apps" versus "add an app" is one decision, not two.
    for (const [actionId, action] of Object.entries(def.actions)) {
      const args = Object.keys(action.params);
      lines.push(
        `- ${def.id}${ACTION_SEP}${actionId} (action): ${action.when}${args.length ? ` (needs: ${args.join(', ')})` : ''}`
      );
    }
  }

  return lines.join('\n');
}

/**
 * The system prompt for the routing pass.
 *
 * Deliberately NOT SystemPromptProvider.compose(). The chat prompt opens with
 * "You are a helpful AI assistant... Be concise and helpful", and a model primed
 * to be helpful will not choose to do nothing - which is how "what is a cat"
 * came back as an account summary. Classification gets its own instructions, and
 * they spend most of their length arguing *for* abstention, because that is the
 * answer the model is biased against and the one that is correct most of the
 * time.
 *
 * NO CONVERSATION CONTEXT. The prompt depends only on the registry and the
 * message being routed, which makes routing a pure function of that message:
 * same sentence, same answer, every time.
 *
 * Two attempts at carrying context both failed. Passing the transcript tail made
 * the router self-reinforcing - a routed turn leaves an assistant note, the next
 * pass reads it as precedent, and everything converges on whatever was picked
 * first. Passing only earlier *user* messages was subtler and worse: a prior
 * "create a new app named X" in the context made the next create read as already
 * satisfied, so the same sentence routed correctly on a fresh page and returned
 * NONE in an established session. Since decoding is greedy, that context was the
 * only thing that varied - and it was the whole reason routing looked flaky.
 *
 * It was there so a follow-up like "sort that by spend" could resolve. Nothing
 * uses that yet. If it is ever needed, it should arrive as a deliberate,
 * structured hand-off from the turn that rendered the view - not as loose prose
 * appended to a classifier prompt.
 *
 * Returns null when nothing is registered; the caller should skip the pass.
 */
export function routingPrompt() {
  const catalog = catalogPrompt();
  if (!catalog) return null;

  return [
    'You are a router. Decide what the user\'s last message is asking for.',
    '',
    // This said "views" throughout, and it cost a real miss: asked whether
    // "create a new app named X" was a request to RENDER A VIEW, the model
    // correctly answered no. Options are intents - some show something, some do
    // something - and the wording has to admit both.
    'Some options show something; others perform an action. Both are listed together,',
    'and an action is marked (action).',
    '',
    'Options:',
    catalog,
    // "or how to do something here" used to end this line, and it competed with
    // every action - "create a new app" reads as "how to do something", marking
    // both and defeating the single-mark safety net. EXPLAIN is for questions
    // the user wants ANSWERED, never for work they want DONE.
    `- ${EXPLAIN}: the user asks a question ABOUT this application and wants an answer in words -`,
    '  what a term means, how something works, why something happened',
    `- ${NO_TEMPLATE}: anything unrelated to this application`,
    '',
    // Strictness governs STEP 1 only. It used to trail the commit instruction,
    // so the last thing read before generating was "when in doubt answer NONE" -
    // and the model would mark MY_APPS:CREATE_APP true, then commit NONE anyway.
    // The scratchpad exists so the decision is made once, in the marks; the
    // commit must be a lookup of those marks, not a fresh judgement under
    // abstention pressure.
    // ONE instruction. There is no commit step: the model marks, and
    // decideFromMarks() picks the winner. Asking for a final id as well produced
    // answers that contradicted the marks the model had just written.
    'For each option, mark true only if the message is specifically asking for THAT option.',
    'Be strict: do not mark an option true merely because it is the closest one.',
    `A QUESTION about this application is ${EXPLAIN}, even when it mentions something another`,
    'option covers - "show me my apps" is a request, "what does DRAFT mean" is a question.',
    'Mark every option false for greetings, small talk, and anything unrelated to this',
    'application. Marking everything false is a normal and correct outcome.'
  ].join('\n');
}

// ---------------------------------------------------------------------------
// 3 + 4. HYDRATION - markup for the canvas, a line for the transcript
// ---------------------------------------------------------------------------

/**
 * Read `source` out of a payload. Uses the injected JMESPath engine when present
 * and falls back to dot-path traversal, so `a.b.c` works with no dependency.
 */
function selectSource(expression, payload) {
  if (!expression) return payload;

  if (queryEngine) {
    try {
      return queryEngine(expression, payload);
    } catch (err) {
      console.warn(`[Templates] query "${expression}" failed - falling back to dot path`, err);
    }
  }

  return expression.split('.').reduce((node, key) => (node == null ? node : node[key]), payload);
}

/**
 * Resolve a component id into renderable output.
 *
 * @param {string} id
 * @param {object} [params]  - model-supplied params, validated before use
 * @param {any}    [payload] - data already in hand; ignored when the entry fetches
 * @returns {Promise<{ ok: boolean, id: string, html: string, transcript: string, errors: string[] }>}
 *
 * Never throws. A template that blows up produces an error result the caller can
 * render as an ordinary bubble - a bad view must not take down the conversation.
 */
export async function hydrate(id, params = {}, payload = null) {
  const def = registry.get(id);

  if (!def) {
    return {
      ok: false,
      id,
      html: '',
      transcript: `Tried to show "${id}", which is not a registered view.`,
      errors: [`unknown template "${id}"`]
    };
  }

  const checked = validateParams(id, params);

  try {
    const raw = def.fetch ? await def.fetch(checked.params) : payload;
    const data = selectSource(def.source, raw);

    return {
      ok: true,
      id,
      html: String(def.render(data, checked.params) ?? ''),
      transcript: String(def.transcript(data, checked.params) ?? ''),
      errors: checked.errors
    };
  } catch (err) {
    console.error(`[Templates] "${id}" failed to hydrate`, err);
    return {
      ok: false,
      id,
      html: '',
      transcript: `The "${id}" view failed to load, so the user did not see it.`,
      errors: [...checked.errors, err.message]
    };
  }
}

export default {
  NO_TEMPLATE,
  EXPLAIN,
  ACTION_SEP,
  routingOptions,
  routableIds,
  parseRoutable,
  getAction,
  validateActionParams,
  toActionSchema,
  toActionSchemaJSON,
  isFullyQualifiedUrl,
  setQueryEngine,
  esc,
  attr,
  registerTemplate,
  unregisterTemplate,
  getTemplate,
  navIntent,
  listTemplates,
  templateIds,
  clearTemplates,
  toResponseSchema,
  toResponseSchemaJSON,
  toRoutingGrammar,
  validateParams,
  catalogPrompt,
  routingPrompt,
  capabilityTips,
  hydrate
};
